/**
 * React Query mutations for the Persona Wizard (P1.T6).
 *
 * Wraps the three backend endpoints (P1.T5):
 *   POST /api/personas/wizard
 *   POST /api/persona-bundles/{id}/simulate
 *
 * Errors are surfaced via the standard apiClient → toast path. Each
 * mutation exposes `mutateAsync` so the wizard page can await the
 * result inline without juggling extra state.
 */

import { useMutation } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import type {
  SimulateRequest,
  SimulateResponse,
  WizardAnswers,
  WizardResponse,
} from '@/types/personaWizard'

async function runWizard(answers: WizardAnswers): Promise<WizardResponse> {
  const response = await apiClient.fetch('/api/personas/wizard', {
    method: 'POST',
    body: JSON.stringify(answers),
  })
  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new Error(`Wizard failed (${response.status}): ${body}`)
  }
  return response.json()
}

async function simulateBundle(
  bundleId: string,
  body: SimulateRequest
): Promise<SimulateResponse> {
  const response = await apiClient.fetch(
    `/api/persona-bundles/${bundleId}/simulate`,
    { method: 'POST', body: JSON.stringify(body) }
  )
  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new Error(`Simulate failed (${response.status}): ${body}`)
  }
  return response.json()
}

async function discardPersona(personaId: string): Promise<void> {
  // Frontend-side discard for "I don't like this wizard result" — calls
  // the existing DELETE /api/personas/{id} (soft-delete by default).
  const response = await apiClient.fetch(`/api/personas/${personaId}`, {
    method: 'DELETE',
  })
  // 204 expected. Don't throw for 404 either — if the user double-clicks
  // discard, second click is a no-op.
  if (!response.ok && response.status !== 404) {
    const body = await response.text().catch(() => '')
    throw new Error(`Discard failed (${response.status}): ${body}`)
  }
}

export function usePersonaWizard() {
  const wizard = useMutation({ mutationFn: runWizard })
  const simulate = useMutation({
    mutationFn: (args: { bundleId: string; body: SimulateRequest }) =>
      simulateBundle(args.bundleId, args.body),
  })
  const discard = useMutation({ mutationFn: discardPersona })

  return { wizard, simulate, discard }
}
